/**
 * GenerationLoader — full-card wait screen shown while a Math Engine
 * stage streams back from the backend. Reads copy from `stageCopy.jsx`.
 *
 * Feb 09 2026 v1: swapped the generic lucide `Loader2` spinner for the
 * branded `<AtomSpinner>` per founder ask.
 * Feb 09 2026 v2 (later same day): added a cycling heartbeat message so
 * the wait has a pulse instead of a static "please wait" energy.
 * Jun 2026: the heartbeat now draws from the shared, stage-neutral
 * generator pool (`GENERATOR_LOADER_MESSAGES`) so it loops indefinitely
 * on a long build without implying progress. The REAL backend `progress`
 * waypoint still overrides it, and the fixed stage preamble (label /
 * title / body from `stageCopy.jsx`) is untouched.
 */
import React from "react";
import { STAGE_COPY } from "./stageCopy";
import { AtomSpinner, GENERATOR_LOADER_MESSAGES, LOADER_ROTATION_MS, useShuffledMessageCycle } from "../AtomLoader";

const GenerationLoader = ({ unit, mini, stage, mode, progress }) => {
  const copy = STAGE_COPY[stage] || STAGE_COPY.activation;
  // Heartbeat now draws from the shared, stage-neutral generator pool so
  // it can loop indefinitely on a long build without implying progress.
  // The REAL backend `progress` waypoint (below) still overrides it, and
  // the fixed stage preamble (label/title/body) is untouched.
  const cycled = useShuffledMessageCycle(GENERATOR_LOADER_MESSAGES, LOADER_ROTATION_MS);
  // Feb 09 pm — real progress hooks. If the backend has reported a
  // waypoint on the job doc, show it verbatim (locking the label to
  // real state). Otherwise fall back to the timer cycle so the wait
  // never sits silent. Founder decision 2a in the plumbing ask_human.
  const label = (typeof progress === "string" && progress.trim()) ? progress.trim() : cycled;
  return (
    <div
      className="ae-card p-10 md:p-14 text-center"
      data-testid="math-engine-loader"
    >
      <div className="flex items-center justify-center mb-4">
        <AtomSpinner size="lg" />
      </div>
      {/* Cycled heartbeat — sits directly under the atom, above the
          fixed preamble. Mint-slate text, italicised, min-height
          reserved so layout does not jump when the message rotates.
          `aria-live=polite` announces the change for screen readers
          without interrupting the current utterance. */}
      <p
        data-testid="math-engine-loader-cycled"
        aria-live="polite"
        className="text-sm font-semibold text-[#4a6984] italic min-h-[1.6em] mb-4"
      >
        <span key={label} className="atom-loader-label-fade">{label}</span>
      </p>
      <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#4a6984]/70">
        Math Engine · {copy.label}
      </p>
      <h3 className="mt-1 font-black text-2xl text-[#1c2d3d] tracking-tight">
        {copy.title}
      </h3>
      <p className="mt-3 text-sm text-[#4a6984]/85 max-w-xl mx-auto leading-relaxed">
        {copy.body(unit, mini)}
      </p>
      <p className="mt-5 text-xs text-[#4a6984]/60">
        This usually takes a minute or two. You can close this tab — the
        result will be saved to your Library automatically.
      </p>
      {stage === "activation" && mode === "start_unit" && (
        <p className="mt-5 text-xs text-[#4a6984]/70 max-w-lg mx-auto">
          Heads up: the full <em>Start the unit</em> chain (diagnostic + Lesson
          1) is shipping in upcoming releases. For now we&apos;ll generate the
          activation — the right first step regardless.
        </p>
      )}
    </div>
  );
};

export default GenerationLoader;
